// Registers the service worker that `vite build` emits from `src/service-worker.ts`. `AppUpdater` calls `register()` on
// startup and uses `onUpdate` to offer the reload once a newer worker is installed and waiting.
//
// The app then loads cache-first, and users see a deployed update only after every tab on the page has been closed or
// they accept the update prompt, since the previous worker keeps serving until then.

type Config = {
	onSuccess?: (registration: ServiceWorkerRegistration) => void
	onUpdate?: (registration: ServiceWorkerRegistration) => void
}

export function register(config?: Config) {
	if (!("serviceWorker" in navigator)) return

	if (import.meta.env.DEV) {
		// The dev server serves no worker (`devOptions.enabled` in vite.config.ts explains why), so any worker found here
		// is left over from before that, and is still answering navigations from its cache. Removing it here spares each
		// developer the manual step, and makes the next navigation a plain network load.
		unregister()
		return
	}

	window.addEventListener("load", () => {
		registerServiceWorker(`${import.meta.env.BASE_URL ?? "/"}service-worker.js`, config)
	})
}

function registerServiceWorker(swUrl: string, config?: Config) {
	navigator.serviceWorker
		.register(swUrl)
		.then((registration) => {
			registration.onupdatefound = () => {
				const installingWorker = registration.installing
				if (installingWorker == null) {
					return
				}
				installingWorker.onstatechange = () => {
					if (installingWorker.state === "installed") {
						if (navigator.serviceWorker.controller) {
							// At this point, the updated precached content has been fetched,
							// but the previous service worker will still serve the older
							// content until all client tabs are closed.
							console.log(
								"New content is available and will be used when all " +
									"tabs for this page are closed. See https://cra.link/PWA.",
							)

							// Execute callback
							if (config && config.onUpdate) {
								config.onUpdate(registration)
							}
						} else {
							// At this point, everything has been precached.
							// It's the perfect time to display a
							// "Content is cached for offline use." message.
							console.log("Content is cached for offline use.")

							// Execute callback
							if (config && config.onSuccess) {
								config.onSuccess(registration)
							}
						}
					}
				}
			}
		})
		.catch((error) => {
			console.error("Error during service worker registration:", error)
		})
}

// `getRegistrations()` rather than `ready`: `ready` never settles when there is nothing registered, and would miss a
// registration whose scope does not cover this page.
export function unregister() {
	if (!("serviceWorker" in navigator)) return
	navigator.serviceWorker
		.getRegistrations()
		.then((registrations) => {
			for (const registration of registrations) void registration.unregister()
		})
		.catch((error: unknown) => {
			console.error("Error during service worker removal:", error)
		})
}
